/**
 * The business's Tickets setup, for the Ticket step's inspector (7 October 2026): the types with their fields
 * (`GET /v1/tickets/types`) and the priorities (`GET /v1/tickets/priorities`) from the tickets service, and the
 * teams (`GET /v1/teams`) from identity, as `_docs/modules/tickets-contract.md` describes them. Typed here,
 * narrowly, because the inspector reads only a few fields; the full shapes belong to the Tickets pages.
 *
 * `failed` is true when the types or priorities cannot be read (the console routes are not up, or tickets are
 * not set up for this organisation): the inspector then says "Set up tickets first" rather than showing empty
 * pickers. Teams are optional: an organisation without any simply has no team to pick.
 */
import { useQuery } from '@tanstack/react-query';
import { http, type Envelope } from '@repletoryreach/sdk-api';

export interface TicketFieldSpec {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  options?: string[];
}

export interface TicketTypeSpec {
  id: string;
  name: string;
  fields?: TicketFieldSpec[];
  default_team_id?: string | null;
  default_priority_id?: string | null;
  sort_order?: number;
  active?: boolean;
}

export interface TicketPrioritySpec {
  id: string;
  name: string;
  rank?: number;
  is_default?: boolean;
}

export interface TicketTeamSpec {
  id: string;
  name: string;
}

const rows = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

export function useTicketSetup() {
  const types = useQuery({
    queryKey: ['tickets', 'types'],
    queryFn: () => http.get<Envelope<TicketTypeSpec[]>>('/v1/tickets/types'),
    staleTime: 60_000,
    retry: false
  });
  const priorities = useQuery({
    queryKey: ['tickets', 'priorities'],
    queryFn: () => http.get<Envelope<TicketPrioritySpec[]>>('/v1/tickets/priorities'),
    staleTime: 60_000,
    retry: false
  });
  const teams = useQuery({
    // Its own key: the inbox caches the same route in another shape.
    queryKey: ['flows', 'ticket-teams'],
    queryFn: () => http.get<Envelope<TicketTeamSpec[]>>('/v1/teams'),
    staleTime: 60_000,
    retry: false
  });
  return {
    pending: types.isPending || priorities.isPending,
    failed: types.isError || priorities.isError,
    types: rows<TicketTypeSpec>(types.data?.data)
      .filter((x) => x && typeof x.id === 'string' && x.active !== false)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    priorities: rows<TicketPrioritySpec>(priorities.data?.data)
      .filter((x) => x && typeof x.id === 'string')
      .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0)),
    teams: rows<TicketTeamSpec>(teams.data?.data)
      .filter((x) => x && typeof x.id === 'string' && typeof x.name === 'string' && x.name.trim())
      .sort((a, b) => a.name.localeCompare(b.name))
  };
}
